import { StyledPhoneInput } from 'components/Common';
import { StyledInput, StyledLabel } from 'components/Common/FormikInput';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { DangerHelpText } from 'components/HelpText';

const name = 'contacts';

export const ClientEditContacts = () => {
  const formik = useFormikContext();
  const { t } = useTranslation();

  const hasError = field => {
    return !!formik.errors?.contacts?.[field];
  };

  const showError = field => {
    return hasError(field);
  };

  const clearError = field => {
    if (formik.errors.contacts?.[field]) {
      formik.setFieldError(`${name}.${field}`, '');
    }
  };

  const handleInputChange = (e, field) => {
    const trimmedValue = e.target.value.trimStart();
    formik.setFieldValue(`${name}.${field}`, trimmedValue);
    if (trimmedValue.length > 0) {
      clearError(field);
    }
  };

  return (
    <>
      {formik.values.clientType === 'legal' && (
        <>
          <Row className="p-0 mb-4 align-items-center">
            <Col xs={3}>
              <StyledLabel className="m-0">{t('client.contact')}</StyledLabel>
            </Col>

            <Col xs={9}>
              <StyledInput
                name={`${name}.contactName`}
                value={formik.values.contacts?.contactName ?? ''}
                placeholder={t('client.contactPlaceholder')}
                onBlur={formik.handleBlur}
                onChange={e => handleInputChange(e, 'contactName')}
                invalid={hasError('contactName')}
              />
              {showError('contactName') && (
                <DangerHelpText noBackground>
                  {formik.errors.contacts.contactName}
                </DangerHelpText>
              )}
            </Col>
          </Row>

          <Row className="p-0 mb-4 align-items-center">
            <Col xs={3}>
              <StyledLabel className="m-0">{t('client.jobTitle')}</StyledLabel>
            </Col>

            <Col xs={9}>
              <StyledInput
                name={`${name}.contactJobTitle`}
                value={formik.values.contacts?.contactJobTitle ?? ''}
                placeholder={t('client.jobTitlePlaceholder')}
                onBlur={formik.handleBlur}
                onChange={e => handleInputChange(e, 'contactJobTitle')}
                invalid={hasError('contactJobTitle')}
              />
              {showError('contactJobTitle') && (
                <DangerHelpText noBackground>
                  {formik.errors.contacts.contactJobTitle}
                </DangerHelpText>
              )}
            </Col>
          </Row>
        </>
      )}

      <Row className="p-0 mb-4 align-items-center">
        <Col xs={3}>
          <StyledLabel className="m-0">{t('client.phone')}</StyledLabel>
        </Col>

        <Col xs={5}>
          <StyledPhoneInput
            value={formik.values.contacts?.phone ?? ''}
            placeholder={t('client.phonePlaceholder')}
            country="de"
            onChange={value => {
              formik.setFieldValue(`${name}.phone`, value);
              if (value) {
                clearError('phone');
              }
            }}
            onBlur={formik.handleBlur}
            errors={
              showError('phone') ? formik.errors.contacts?.phone : undefined
            }
          />
          {showError('phone') && (
            <DangerHelpText noBackground>
              {formik.errors.contacts.phone}
            </DangerHelpText>
          )}
        </Col>
      </Row>

      {formik.values.clientType === 'legal' && (
        <Row className="p-0 mb-4 align-items-center">
          <Col xs={3}>
            <StyledLabel className="m-0">{t('client.fax')}</StyledLabel>
          </Col>

          <Col xs={5}>
            <StyledPhoneInput
              value={formik.values.contacts?.fax ?? ''}
              placeholder={t('client.faxPlaceholder')}
              country="de"
              onChange={value => formik.setFieldValue(`${name}.fax`, value)}
              errors={formik.errors?.contacts?.fax}
            />
          </Col>
        </Row>
      )}

      <Row className="p-0 mb-4 align-items-center">
        <Col xs={3}>
          <StyledLabel className="m-0">{t('auth.email')}</StyledLabel>
        </Col>

        <Col xs={9}>
          <StyledInput
            name={`${name}.email`}
            value={formik.values.contacts?.email ?? ''}
            placeholder={t('auth.enterEmail')}
            onBlur={formik.handleBlur}
            onChange={e => handleInputChange(e, 'email')}
            invalid={hasError('email')}
          />
          {showError('email') && (
            <DangerHelpText noBackground>
              {formik.errors.contacts.email}
            </DangerHelpText>
          )}
        </Col>
      </Row>

      {formik.values.clientType === 'legal' && (
        <Row className="p-0 mb-4 align-items-center">
          <Col xs={3}>
            <StyledLabel className="m-0">
              {t('client.billingEmail')}
            </StyledLabel>
          </Col>

          <Col xs={5}>
            <StyledInput
              maxLength={600}
              value={formik.values.contacts?.billingEmail ?? ''}
              name={`${name}.billingEmail`}
              onBlur={formik.handleBlur}
              onChange={e => handleInputChange(e, 'billingEmail')}
              placeholder={t('client.billingEmailPlaceholder')}
              type="textarea"
              $textarea={true}
              invalid={hasError('billingEmail')}
            />
            {showError('billingEmail') && (
              <DangerHelpText noBackground>
                {formik.errors.contacts.billingEmail}
              </DangerHelpText>
            )}
          </Col>
        </Row>
      )}

      {formik.values.clientType === 'legal' && (
        <Row className="p-0 mb-4 align-items-center">
          <Col xs={3}>
            <StyledLabel className="m-0">
              {t('client.billingEmailCc')}
            </StyledLabel>
          </Col>

          <Col xs={5}>
            <StyledInput
              maxLength={600}
              value={formik.values.contacts?.billingEmailCc ?? ''}
              name={`${name}.billingEmailCc`}
              onBlur={formik.handleBlur}
              onChange={e => handleInputChange(e, 'billingEmailCc')}
              placeholder={t('client.billingEmailCcPlaceholder')}
              type="textarea"
              $textarea={true}
              invalid={hasError('billingEmailCc')}
            />
            {showError('billingEmailCc') && (
              <DangerHelpText noBackground>
                {formik.errors.contacts.billingEmailCc}
              </DangerHelpText>
            )}
          </Col>
        </Row>
      )}
    </>
  );
};
